export type FormField = {
  id: string; // Unique identifier for the field
  label: string;
  type: string;
  /**
   * Which canonical fact this field represents, if any — a second, stable name that survives the
   * admin renaming `label`. Optional by design: most fields represent nothing outside their own
   * form and should never carry one, and a form with none tagged behaves exactly as it always has.
   * A `field_key` MAY repeat across fields on one form (two dietary questions, one per restaurant);
   * an `id` never does, which is why `response_data` is keyed by `id` and not by this.
   * Values are validated against the registry in
   * `@/features/forms-builder-v2/_logic/field-keys` — treat an unrecognized key as untagged.
   */
  field_key?: string | null;
  /**
   * Who decided this field's `field_key`: an admin picking it in the builder ("manual"), or the
   * tagging agent that runs when a form's field structure is published or edited ("agent"). Always
   * written alongside `field_key` so a suggested tag is never mistaken for a deliberate one.
   *
   * Typed as a plain string, not as that union: this comes out of jsonb, where nothing enforces
   * either value, and a union here would have the compiler vouch for data it has never seen. Read
   * it through a guard, the way `field_key` is read through `isFieldKey`.
   */
  field_key_source?: string | null;
  version: number; // Version number for the field
  choices?: string[];
  is_required: boolean;
  description?: string; // Sub-description shown below the field label (can contain HTML with inline links)
  section?: string; // Section name this field belongs to
  page?: string; // Optional page key for paginated forms — sections sharing a key render on one page
  conditional_logic?: {
    skip_to_end?: boolean; // If true, skip to end of form when this condition is met
    skip_to_section?: string; // If set, skip to this section instead of end
    condition_value?: string; // The value that triggers the condition (for dropdowns/choices)
  };
  // Reveal condition: this field is only visible when either another field's answer
  // (legacy/default shape, discriminated by the presence of `field_id` — no data
  // migration needed for existing forms) or the respondent's resolved respondent type
  // is one of `values`. Hidden while the controlling answer is unanswered/off-list, or
  // no respondent type has been resolved to a listed value.
  show_if?:
    | { field_id: string; values: string[] } // id of the controlling field; its answers reveal this field
    | { source: "respondent_type"; values: number[] }; // form_respondent_types.id — stable under label rename
  has_other_option?: boolean; // If true, adds "Other" option with text field for custom input
  date_constraints?: {
    // For `date` fields only: makes this field's allowed range depend on another date field.
    min_field_id?: string; // This field's value must be on or after the referenced field's value
    max_field_id?: string; // This field's value must be on or before the referenced field's value
  };
  // For `scale` fields: a numeric rating between min and max. The answer is stored as a
  // plain number, or `null` when the respondent picks the optional "N/A" choice.
  scale_config?: {
    min: number;
    max: number;
    min_label?: string; // Caption under the low end (e.g. "Not likely")
    max_label?: string; // Caption under the high end (e.g. "Very likely")
    na_label?: string; // Label for the null/"didn't attend" choice (default "N/A")
    display?: "buttons" | "stars"; // Render as number buttons (default) or star icons
  };
  has_comment?: boolean; // If true, shows an optional free-text comment under the field.
  comment_label?: string; // Label for the comment box (default "Comment (optional)")
  // The comment answer is stored separately in response_data under `${label} (comment)`.
  max_length?: number; // Max character length for text answers (and the comment, if enabled)
  multiline?: boolean; // Render a text field as a multi-line textarea (for long-form answers)
  section_description?: string; // Intro text shown once above this field's section (HTML/links ok)
  // For `type: "rich_text_content"` items only: sanitized TipTap HTML rendered in place of a
  // question — never answered, never required. See _logic/field-types.ts for the closed set of
  // non-answerable types and _logic/rich-text-sanitize.ts for how `content` is sanitized on write.
  content?: string;
};

/** A form-builder item type that renders formatted text instead of a question. */
export const RICH_TEXT_CONTENT_FIELD_TYPE = "rich_text_content" as const;

/**
 * A conditional confirmation message. Branches the form's post-submission confirmation
 * copy on how a single question was answered: when the field's answer matches one of
 * `values`, this rule's `message` replaces the form's default `confirmation_message`
 * (and, if `hide_header`, suppresses the generic "Thank you!" header). Rules are
 * evaluated in order and the first match wins — see resolveConfirmationMessage.
 */
export type FormConfirmationRule = {
  /** Stable id for React keys / editing (not otherwise meaningful). */
  id: string;
  /** The `FormField.id` whose answer this rule branches on. */
  field_id: string;
  /** Answer value(s) that trigger this message (matched against the field's answer). */
  values: string[];
  /** The confirmation body shown when the rule matches (HTML allowed, like confirmation_message). */
  message: string;
  /** When true, hide the "Thank you!" header so the message can open with its own wording. */
  hide_header?: boolean;
};

/** Suffix appended to a field label to key its optional comment in response_data. */
export const COMMENT_KEY_SUFFIX = " (comment)";

/**
 * The response_data key under which a field's optional comment is stored.
 * Keyed by label (like the field's own answer) — so a field literally labelled
 * `"X (comment)"` would collide with field `"X"`'s comment key. Form labels are
 * effectively unique in practice; avoid the reserved ` (comment)` suffix in labels.
 * @param label - The field's label
 * @returns The sibling key, e.g. "How was it? (comment)"
 */
export function commentKeyForLabel(label: string): string {
  return `${label}${COMMENT_KEY_SUFFIX}`;
}

import type { FileFieldMapping, FileTemplateKind } from "@/features/forms/_logic/types/file-mapping-types";
import type { FilledFilesFormat } from "@/features/forms/_logic/constants/file-constants";
import type { ReminderScheduleConfig } from "@/features/forms/_logic/types/reminder-schedule-types";

export interface Form {
  form_id: number;
  event_id: number;
  owner_user_id: number;
  form_fields: FormField[];
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  location: string;
  custom_logo_path: string;
  /** Display size for the form's own logo: "small" | "large" | null (null = default sizing). */
  logo_size: string | null;
  is_public: boolean;
  /**
   * When set (and the form is invited-only), anyone with a verified email on this
   * domain is auto-authorized without an attendee row or invite — mirrors the
   * `sso_domain` enforcement on event_landing_pages. NULL = no SSO grant.
   * Server-only: never serialized into the browser-facing form projection.
   */
  sso_domain?: string | null;
  /**
   * When true, any authenticated Nowadays user can view/submit the form — independent
   * of `is_public`, the invited/responded roster, and `sso_domain`. Defaults to false.
   */
  require_login?: boolean;
  allowed_email_domains?: string[];
  unique_emails?: boolean;
  view_count?: number;
  sheets_sync_enabled?: boolean;
  sheets_spreadsheet_id?: string;
  sheets_sheet_id?: string;
  sheets_sheet_title?: string;
  /**
   * Import-direction Google Sheet sync (distinct from the sheets_* export-direction
   * fields above). Polled by a cron to pre-fill `form_responses` for new invitees, and
   * optionally auto-send them an invite.
   */
  invite_sheets_spreadsheet_id?: string;
  invite_sheets_sheet_id?: string;
  invite_sheets_sheet_title?: string;
  invite_sheets_email_column?: string;
  /** Additional sheet-column -> form-field-label mappings for form_responses prefill (beyond email). */
  invite_sheets_field_mapping?: Record<string, string>;
  invite_sheets_sync_enabled?: boolean;
  invite_sheets_auto_send?: boolean;
  /**
   * Which email template the auto-send uses: a system template type (currently only
   * "invitation") or a "saved:<id>" reference into `email_templates.saved_custom_templates`.
   * NULL/absent = the form's invitation template (the prior hard-coded behavior).
   */
  invite_sheets_email_template?: string | null;
  invite_sheets_last_attempt_at?: string;
  invite_sheets_last_added_count?: number;
  /** Fixed generic status code (e.g. "sheet_unreachable") — never raw provider/DB error text. */
  invite_sheets_last_error?: string | null;
  email_templates?: {
    reminder?: { subject: string; body: string };
    confirmation?: { subject: string; body: string };
    approval?: { subject: string; body: string };
    rejection?: { subject: string; body: string };
    qr_code?: { subject: string; body: string };
    file_delivery?: { subject: string; body: string; attachFormat?: FilledFilesFormat };
  };
  confirmation_message?: string;
  /**
   * Conditional confirmation messages, evaluated in order (first match wins) against the
   * submitted answers. Each rule can replace `confirmation_message` and suppress the
   * "Thank you!" header for responses that answered a given question a given way (e.g. an
   * RSVP "No"). NULL/empty falls back to `confirmation_message` for every response — so
   * existing forms are unaffected. See resolveConfirmationMessage.
   */
  confirmation_rules?: FormConfirmationRule[];
  paginated?: boolean; // When true, the public form renders one section per page with Next/Back
  qr_code_enabled?: boolean;
  qr_send_to_all?: boolean;
  payment_enabled?: boolean;
  payment_amount_display?: string;
  is_closed?: boolean;
  closes_at?: string | null;
  closed_message?: string | null;
  closed_at?: string | null;
  closed_by_user_id?: number | null;
  /** Single-writer (form-settings save action only) — see ReminderScheduleConfig for why. */
  reminder_schedule?: ReminderScheduleConfig | null;
  /** Kept in sync with `reminder_schedule.enabled` so the reminder cron can filter in SQL. */
  reminder_enabled?: boolean;
  // File autofill + delivery fields
  file_template_path?: string;
  file_template_kind?: FileTemplateKind;
  file_field_mapping?: FileFieldMapping;
  file_delivery_subject?: string;
  file_delivery_body?: string;
  file_delivery_send?: boolean;
  file_delivery_test_sent_at?: string;
  /** Non-null enables a Slack notification on every submit AND edit/resubmit; FK into nowadays.slack_notification_channels. */
  slack_notify_channel_id?: number | null;
  /** Form field label surfaced in the notification; shows what it changed from on an edit. */
  slack_notify_watch_field?: string | null;
  /** Unused — the notification now tallies every value of `slack_notify_watch_field` automatically. */
  slack_notify_count_value?: string | null;
}

export type Attachment = {
  url: string;
  name: string;
  type: string;
  timestamp: string;
  download_url: string;
};

/** A planner-defined respondent segment for a form (e.g. VIP, Partner, General). */
export interface FormRespondentType {
  id: number;
  form_id: number;
  label: string;
  sort_order: number;
  /** The untagged/fallback bucket — exactly one per form should be true. */
  is_default: boolean;
  /** Price in cents for this tier. Null means free. */
  price_cents: number | null;
  currency: string;
  /** CSPRNG token for this tier's invite link. Null until generated. */
  invite_token: string | null;
  /** Set once any response has paid/pending against this tier — soft-delete marker. */
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}
